import { waitFor } from '../../../CodesThatsGottaGoSomewhereElse';
import { ObjectManager } from '../../../concepts/objects/ObjectManager';
import { Resolver } from '../../Container';
import { Camera } from '../../canvas/Camera';
import { ConfettiCannon } from '../../canvas/ConfettiCannon';
import { EffectDirector, Effects } from '../../canvas/visualization/EffectDirector';
import { Sticky } from '../../packages/paper/Sticky';
import { Shape } from '../../packages/shapes/Shape';
import { Text } from '../../packages/text/Text';

const isEqSign = (a, b) => (a / Math.abs(a)) === (b / Math.abs(b));

export const waitForCameraMovement = (camera: Camera, dim: 'x' | 'y', threshold: number) => {
    let moved = 0;
    let last = camera.position[dim];
    return new Promise<void>((rs) => {
        const b = camera.observe(() => {
            const next = camera.position[dim];
            const delta = next - last;
            if (isEqSign(delta, threshold)) {
                moved += delta;
                if (Math.abs(moved) >= Math.abs(threshold)) {
                    b();
                    rs();
                }
            }
            last = next;
        });
    });
};

export const waitForCameraLeftRight = async (camera: Camera, threshold: number) => {
    await Promise.all([
        waitForCameraMovement(camera, 'x', -threshold),
        waitForCameraMovement(camera, 'x', threshold),
    ]);
};

export const waitForCameraUpDown = async (camera: Camera, threshold: number) => {
    await Promise.all([
        waitForCameraMovement(camera, 'y', -threshold),
        waitForCameraMovement(camera, 'y', threshold),
    ]);
};

export const waitForCameraZoom = (camera: Camera, mode: number) => {
    let last = camera.scale;
    return new Promise<void>((rs) => {
        const b = camera.observe(() => {
            const next = camera.scale;
            const delta = next - last;
            if (isEqSign(delta, mode)) {
                b();
                rs();
            }
            last = next;
        });
    });
};

export const waitForState = async (objects: ObjectManager, checker: () => boolean) => {
    return new Promise<void>((rs) => {
        const off = objects.on('updated', () => {
            if (checker()) {
                off();
                rs();
            }
        });
    });
};

export const waitForConfetti = async (cannon: ConfettiCannon) => {
    return new Promise<void>((rs) => {
        const off = cannon.on('fire', () => {
            off();
            rs();
        });
    });
};

export const waitForUIElement = async (element: string) => {
    return new Promise<void>((rs) => {
        const m = new MutationObserver(() => {
            if (document.querySelector(element)) {
                m.disconnect();
                rs();
            }
        });

        m.observe(document.body, {
            childList: true,
            subtree: true,
        });
    });
};

export const waitForUIElementValue = async (element: string, value: string) => {
    return new Promise<void>((rs) => {
        const m = new MutationObserver(() => {
            const textFromElement = ((document.querySelector(element) as HTMLElement).innerText).trim();

            if (textFromElement === value) {
                m.disconnect();
                rs();
            }
        });

        m.observe(document.body, {
            childList: true,
            subtree: true,
        });
    });
};

export const waitForElementClick = async (element: string) => {
    return new Promise<void>((rs) => {
        const handleClick = () => {
            const el = document.querySelector(element);
            if (el) {
                el.removeEventListener('click', handleClick);
                m.disconnect();
                rs();
            }
        };

        const m = new MutationObserver(() => {
            const el = document.querySelector(element);
            if (el) {
                el.addEventListener('click', handleClick);
            }
        });

        m.observe(document.body, {
            childList: true,
            subtree: true,
        });
    });
};

export const deleteText = (objects: ObjectManager, sentences: string[]) => {
    const oldText = Array.from(objects.getAll(Text)).filter(t =>
        sentences.some(s => t.content.includes(s))
    );

    objects.update(u =>
        oldText.forEach(t => {
            u.delete(t);
        })
    );
};

export const deleteAllText = (objects: ObjectManager) => {
    const text = objects.getAll(Text);
    objects.update(u =>
        text.forEach(t => {
            u.delete(t);
        })
    );
};

export const deleteAllShapes = (objects: ObjectManager) => {
    const shapes = objects.getAll(Shape);
    objects.update(u =>
        shapes.forEach(s => {
            u.delete(s);
        })
    );
};

export const revealStickiesWithDelay = async (objects: ObjectManager, stickies: Sticky[], index: number = 0) => {
    if (index >= stickies.length) {
        return;
    }

    await waitFor(350);

    objects.update(() => {
        stickies[index].concealed = false;
    });

    await waitFor(350).then(
        () => revealStickiesWithDelay(objects, stickies, index + 1)
    );
};

export const popInInstructions = async (r: Resolver) => {
    const objects = r.call(ObjectManager);
    const effects = r.call(EffectDirector);

    // Find all text, add a popin
    const elmts = [...Array.from(objects.getAll(Text)), ...Array.from(objects.getAll(Shape))];
    effects.trigger(Effects.popIn, Array.from(elmts));

    await waitFor(500);
};
